import type { EventItem, EventKind } from '../types';
import type { IconName } from '../ui/icon-names';
import { uuid } from '../utils/uuid';

// ────────────────────────────────────────────────────────────────────────────
// Central registry for event-type metadata. A single source of truth for
// icons, labels, tones, and "create a blank event of this kind" helpers.
// Keeps the UI consistent across the event picker, timeline chips, and
// editors.
// ────────────────────────────────────────────────────────────────────────────

export interface EventMeta {
  kind: EventKind;
  label: string;
  icon: IconName;
  /** Short one-line description shown in the event-type picker. */
  blurb: string;
  /** Chip color tone: used in timeline chips and listings. */
  tone: 'neg' | 'pos' | 'accent' | 'warning' | 'muted' | 'neutral';
  /** Category group for the picker. */
  group: 'spending' | 'income' | 'housing' | 'retirement';
}

export const EVENT_META: Record<EventKind, EventMeta> = {
  purchase: {
    kind: 'purchase',
    label: 'Large purchase',
    icon: 'purchase',
    blurb: 'Car, tuition, renovation, or any big one-time cost',
    tone: 'neg',
    group: 'spending',
  },
  gift: {
    kind: 'gift',
    label: 'Gift / donation',
    icon: 'gift',
    blurb: 'Give money away — to family, charity, or a cause',
    tone: 'neg',
    group: 'spending',
  },
  home_buy: {
    kind: 'home_buy',
    label: 'Buy a home',
    icon: 'home-buy',
    blurb: 'New primary residence with a mortgage',
    tone: 'accent',
    group: 'housing',
  },
  home_sell: {
    kind: 'home_sell',
    label: 'Sell home',
    icon: 'home-sell',
    blurb: 'Sell without replacement, §121 exclusion optional',
    tone: 'pos',
    group: 'housing',
  },
  refinance: {
    kind: 'refinance',
    label: 'Refinance mortgage',
    icon: 'refi',
    blurb: 'New rate/term on the current mortgage',
    tone: 'accent',
    group: 'housing',
  },
  home_improvement: {
    kind: 'home_improvement',
    label: 'Home improvement',
    icon: 'home',
    blurb: 'Renovation: cost + added home value + cost-basis bump for §121',
    tone: 'accent',
    group: 'housing',
  },
  income_change: {
    kind: 'income_change',
    label: 'Salary change',
    icon: 'income',
    blurb: 'New salary — raise, pay cut, new job, Barista FIRE',
    tone: 'pos',
    group: 'income',
  },
  contribution_change: {
    kind: 'contribution_change',
    label: 'Savings change',
    icon: 'tag',
    blurb: 'Set new annual contribution amounts to 401(k) / taxable / HYSA',
    tone: 'accent',
    group: 'income',
  },
  spending_change: {
    kind: 'spending_change',
    label: 'Spending change',
    icon: 'tag',
    blurb: 'New monthly household spend (downsize lifestyle, kids out, etc.)',
    tone: 'accent',
    group: 'income',
  },
  job_gap: {
    kind: 'job_gap',
    label: 'Job gap / sabbatical',
    icon: 'job-gap',
    blurb: 'Temporary no-income window; live off savings',
    tone: 'warning',
    group: 'income',
  },
  windfall: {
    kind: 'windfall',
    label: 'Windfall',
    icon: 'windfall',
    blurb: 'Inheritance, bonus, business sale',
    tone: 'pos',
    group: 'income',
  },
  roth_conversion: {
    kind: 'roth_conversion',
    label: 'Roth conversion',
    icon: 'roth',
    blurb: 'Convert pre-tax to Roth; pays tax now, grows tax-free',
    tone: 'accent',
    group: 'retirement',
  },
  social_security: {
    kind: 'social_security',
    label: 'Social Security',
    icon: 'pension',
    blurb: 'Claim SS benefits at this age',
    tone: 'pos',
    group: 'retirement',
  },
  pension: {
    kind: 'pension',
    label: 'Pension',
    icon: 'pension',
    blurb: 'Fixed monthly pension payments',
    tone: 'pos',
    group: 'retirement',
  },
  retire: {
    kind: 'retire',
    label: 'Change retirement age',
    icon: 'retire',
    blurb: 'Retire earlier or later than the assumption',
    tone: 'accent',
    group: 'retirement',
  },
};

export const EVENT_KINDS_BY_GROUP: {
  group: EventMeta['group'];
  label: string;
  kinds: EventKind[];
}[] = [
  {
    group: 'spending',
    label: 'Spending',
    kinds: ['purchase', 'gift'],
  },
  {
    group: 'housing',
    label: 'Housing',
    kinds: ['home_buy', 'home_sell', 'home_improvement', 'refinance'],
  },
  {
    group: 'income',
    label: 'Income & savings',
    kinds: ['income_change', 'contribution_change', 'spending_change', 'job_gap', 'windfall'],
  },
  {
    group: 'retirement',
    label: 'Retirement',
    kinds: ['roth_conversion', 'social_security', 'pension', 'retire'],
  },
];

/** Pretty-print an event's "headline" (amount or key value) for timelines. */
export function eventHeadline(e: EventItem): string {
  switch (e.kind) {
    case 'purchase':
    case 'gift':
      return `$${Math.round(e.amount).toLocaleString()}`;
    case 'home_buy':
      return `$${Math.round(e.purchasePrice).toLocaleString()}`;
    case 'home_sell':
      return `$${Math.round(e.salePrice).toLocaleString()}`;
    case 'refinance':
      return `${(e.newAnnualRate * 100).toFixed(2)}% · ${e.newTermYears}y`;
    case 'home_improvement':
      return `$${Math.round(e.cost).toLocaleString()} · +$${Math.round(e.valueAdded).toLocaleString()} value`;
    case 'income_change':
      return `salary → $${Math.round(e.newAnnualSalary).toLocaleString()}/yr`;
    case 'contribution_change': {
      const parts: string[] = [];
      if (e.newRetirement !== undefined) parts.push(`401k $${Math.round(e.newRetirement).toLocaleString()}`);
      if (e.newTaxable !== undefined) parts.push(`Tax $${Math.round(e.newTaxable).toLocaleString()}`);
      if (e.newHysa !== undefined) parts.push(`HYSA $${Math.round(e.newHysa).toLocaleString()}`);
      return parts.join(' · ') || '—';
    }
    case 'spending_change':
      return `spend → $${Math.round(e.newMonthlySpend).toLocaleString()}/mo`;
    case 'job_gap':
      return `${e.durationYears}y off`;
    case 'windfall':
      return `+$${Math.round(e.amount).toLocaleString()}`;
    case 'roth_conversion':
      return `$${Math.round(e.amount).toLocaleString()}`;
    case 'social_security':
      return `$${Math.round(e.monthlyBenefit).toLocaleString()}/mo`;
    case 'pension':
      return `$${Math.round(e.monthlyBenefit).toLocaleString()}/mo`;
    case 'retire':
      return `age ${e.retirementAge}`;
  }
}

/** Default name shown when the user hasn't specified one. */
export function defaultEventName(kind: EventKind): string {
  return EVENT_META[kind].label;
}

/** Build a blank event of the given kind with sensible defaults. */
export function blankEvent(kind: EventKind, yearOffset = 1): EventItem {
  const base = {
    id: uuid(),
    yearOffset,
    enabled: true,
    createdAt: new Date().toISOString(),
  };
  switch (kind) {
    case 'purchase':
      return { ...base, kind: 'purchase', name: 'Large purchase', amount: 50_000 };
    case 'gift':
      return { ...base, kind: 'gift', name: 'Gift', amount: 10_000 };
    case 'home_buy':
      return {
        ...base,
        kind: 'home_buy',
        name: 'New home',
        purchasePrice: 800_000,
        downPayment: 200_000,
        mortgagePrincipal: 600_000,
        mortgageAnnualRate: 0.065,
        mortgageTermYears: 30,
        sellCurrentHome: true,
        newCostBasis: 800_000,
      };
    case 'home_sell':
      return {
        ...base,
        kind: 'home_sell',
        salePrice: 600_000,
        useSection121: true,
        proceedsBucket: 'hysa',
      };
    case 'home_improvement':
      return {
        ...base,
        kind: 'home_improvement',
        name: 'Kitchen renovation',
        cost: 50_000,
        // Kitchens typically return 60-75% of cost in resale value.
        valueAdded: 35_000,
        addToCostBasis: true,
      };
    case 'refinance':
      return {
        ...base,
        kind: 'refinance',
        newAnnualRate: 0.055,
        newTermYears: 30,
      };
    case 'income_change':
      // Leave the salary blank-ish — the editor fills with the current salary
      // so the user can tweak rather than guess.
      return {
        ...base,
        kind: 'income_change',
        name: 'New salary',
        newAnnualSalary: 0,
      };
    case 'contribution_change':
      // All fields undefined — nothing changes. Editor prompts the user to
      // flip the ones they want.
      return {
        ...base,
        kind: 'contribution_change',
        name: 'Savings change',
      };
    case 'spending_change':
      return {
        ...base,
        kind: 'spending_change',
        name: 'Spending change',
        newMonthlySpend: 0,
      };
    case 'job_gap':
      return {
        ...base,
        kind: 'job_gap',
        name: 'Sabbatical',
        durationYears: 1,
      };
    case 'windfall':
      return {
        ...base,
        kind: 'windfall',
        name: 'Windfall',
        amount: 100_000,
        toBucket: 'taxable',
        taxFree: true,
      };
    case 'roth_conversion':
      return { ...base, kind: 'roth_conversion', amount: 50_000 };
    case 'social_security':
      return {
        ...base,
        kind: 'social_security',
        monthlyBenefit: 2_500,
      };
    case 'pension':
      return {
        ...base,
        kind: 'pension',
        monthlyBenefit: 1_500,
      };
    case 'retire':
      return {
        ...base,
        kind: 'retire',
        retirementAge: 55,
      };
  }
}
